import { motion } from 'framer-motion';
import { Calendar, MapPin, Clock, ExternalLink, Share2, Star, Mic } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ShareButton from '@/components/ShareButton';
import { useNavigate } from 'react-router-dom';

const VENUE_URLS = {
  "Gluek's": "https://www.glueks.com/",
  "Day Block Brewing": "https://dayblockbrewing.com/",
  "Green Room": "https://www.greenroommn.com/events",
  "Bunker's": "https://bunkersmusic.com/calendar",
  "Dakota": "https://www.dakotacooks.com/events/"
};

const EventCard = ({ event, size = 'normal' }) => {
  const navigate = useNavigate();
  
  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const baseUrl = window.location.origin;
  const eventUrl = `${baseUrl}/calendar?event=${event.id}`;
  
  const isInternalLink = event.ticket_url && event.ticket_url.startsWith('/');
  const isVocalistShowcase = event.is_vocalist_showcase;
  const venueUrl = event.venue_url || VENUE_URLS[event.venue] || null;

  const handleTicketClick = (e) => {
    e.stopPropagation();
    if (isInternalLink) {
      navigate(event.ticket_url);
    } else {
      window.open(event.ticket_url, '_blank');
    }
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={`group relative rounded-sm overflow-hidden bg-card border ${event.is_sponsored ? 'border-yellow-500/50 ring-1 ring-yellow-500/30' : 'border-border'} card-hover ${
        size === 'large' ? 'row-span-2' : ''
      }`}
      data-testid={`event-card-${event.id}`}
    >
      <div className={`relative ${size === 'large' ? 'h-full min-h-[300px]' : 'aspect-[4/3]'}`}>
        <img
          src={event.image_url || 'https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=800'}
          alt={event.title}
          className="w-full h-full object-cover"
        />
        
        {/* Sponsored badge */}
        {event.is_sponsored && (
          <div className="absolute top-4 right-4 bg-yellow-500 text-black px-2 py-1 rounded-sm flex items-center gap-1">
            <Star size={12} fill="currentColor" />
            <span className="font-bold text-xs">SPONSORED</span>
          </div>
        )}
        
        {/* Sponsor name */}
        {event.sponsor_name && (
          <div className="absolute bottom-[85px] right-3 text-[10px] text-yellow-300/80 font-medium">
            Presented by {event.sponsor_name}
          </div>
        )}
        
        {/* Date badge */}
        <div className="absolute top-4 left-4 bg-primary px-3 py-1 rounded-sm">
          <p className="font-heading font-bold text-sm">{formatDate(event.date)}</p>
        </div>

        {/* Info overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4 gradient-scrim">
          <h3 className="font-heading font-bold text-lg mb-2 line-clamp-2">{event.title}</h3>
          
          <div className="space-y-1 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <MapPin size={14} />
              <span className="truncate">{event.venue}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={14} />
              <span>{event.time}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-3">
            {event.ticket_url && (
              <Button
                size="sm"
                className={`gap-2 ${isVocalistShowcase ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600' : ''}`}
                onClick={handleTicketClick}
                data-testid={`event-ticket-btn-${event.id}`}
              >
                {isVocalistShowcase ? (
                  <>
                    <Mic size={14} />
                    Apply Now
                  </>
                ) : (
                  <>
                    Get Tickets
                    <ExternalLink size={14} />
                  </>
                )}
              </Button>
            )}
            {venueUrl && (
              <a
                href={venueUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300 transition-colors"
                data-testid={`event-venue-link-${event.id}`}
              >
                More Info <ExternalLink size={12} />
              </a>
            )}
            <ShareButton
              title={`${event.title} - Twin Tube`}
              description={`${event.title} at ${event.venue} on ${formatDate(event.date)} - ${event.time}. Discover events in the Twin Cities!`}
              url={eventUrl}
              size="sm"
              variant="ghost"
              className="text-muted-foreground hover:text-foreground"
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default EventCard;
